import { request } from '@/internationalization/api-client'
import type { PermissionDescriptor, RoleSummary } from './permissions'

export interface MyAuthorization {
  role: string
  roleName: string
  roleId: string | null
  isProtectedRole: boolean
  permissions: string[]
  /**
   * What this DEPLOYMENT can do, as distinct from what this PERSON may do.
   *
   * A permission says somebody is allowed to ask for a recording; it cannot
   * say whether this environment has a recorder to dispatch, because that is
   * ECS wiring and the role table has never heard of it. Gating on the
   * permission alone is what shipped a "Record a meeting" button that answered
   * 503. Absent or unknown keys read as false -- see `useCapability`.
   */
  capabilities?: Record<string, boolean>
  /**
   * Whether this caller is one of the three people the vocabulary feature is
   * hardcoded to on the server (`domain.vocab.VOCAB_ALLOWED_EMAILS`) -- never
   * a tenant permission, so it cannot be derived from `permissions`.
   */
  canAccessVocabulary?: boolean
  /** One of the three people who read the feedback inbox (server-side list). */
  canAccessFeedbackInbox?: boolean
}

/**
 * What the signed-in person may do, from the authorization service.
 *
 * `/session` already carries the same array so the first render needs no extra
 * round trip. This endpoint is for the cases `/session` cannot cover: a reload
 * restores the session from the refresh cookie (which returns a token, a user
 * and a tenant, but no permissions), and a workspace switch changes the answer
 * without changing the session.
 */
export function fetchMyAuthorization(): Promise<MyAuthorization> {
  return request<MyAuthorization>('/authorization/me')
}

export function fetchRoles(): Promise<RoleSummary[]> {
  return request<RoleSummary[]>('/authorization/roles')
}

/** The catalogue for the role editor. Requires `role:manage`. */
export function fetchPermissionCatalogue(): Promise<PermissionDescriptor[]> {
  return request<PermissionDescriptor[]>('/authorization/permissions')
}

export function createRole(body: {
  name: string
  description?: string | null
  permissions: string[]
}): Promise<RoleSummary> {
  return request<RoleSummary>('/authorization/roles', { method: 'POST', body })
}

export function updateRole(
  roleId: string,
  body: { name?: string; description?: string | null; permissions?: string[] },
): Promise<RoleSummary> {
  return request<RoleSummary>(`/authorization/roles/${roleId}`, {
    method: 'PATCH',
    body,
  })
}

export function deleteRole(roleId: string): Promise<void> {
  return request<void>(`/authorization/roles/${roleId}`, { method: 'DELETE' })
}
